import { format, isValid, parseISO } from 'date-fns';
import { he } from 'date-fns/locale';

/** Milliseconds in a day. */
export const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Any shape a "timestamp" field might arrive in.
 *
 * Firestore Timestamps written through the app SDK expose .toDate()/.toMillis(),
 * but data written outside the app — Admin SDK, import scripts, AI agents, or the
 * Firestore console — frequently lands as a plain {seconds,nanoseconds} object,
 * an Admin-serialized {_seconds,_nanoseconds} object, an ISO string, or epoch
 * millis. Calling .toDate()/.toMillis() on those throws
 * ("x.toDate is not a function") and takes the whole list down with it.
 * Coerce through toDate()/toMillis() instead of touching the methods directly.
 */
export type TimestampLike =
  | { toDate: () => Date }
  | { seconds: number; nanoseconds?: number }
  | { _seconds: number; _nanoseconds?: number }
  | Date
  | string
  | number
  | null
  | undefined;

/**
 * Coerce any timestamp-like value into a Date, or null if it can't be parsed.
 * Never throws.
 */
export function toDate(value: TimestampLike): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }
  if (typeof value === 'string') {
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }
  if (typeof value === 'object') {
    // Real Firestore Timestamp (client or admin SDK instance).
    if ('toDate' in value && typeof value.toDate === 'function') {
      const d = value.toDate();
      return isValid(d) ? d : null;
    }
    // Plain serialized form: {seconds,nanoseconds} or admin's {_seconds,_nanoseconds}.
    const seconds =
      'seconds' in value && typeof value.seconds === 'number'
        ? value.seconds
        : '_seconds' in value && typeof value._seconds === 'number'
          ? value._seconds
          : undefined;
    if (seconds !== undefined) {
      const nanos =
        'nanoseconds' in value && typeof value.nanoseconds === 'number'
          ? value.nanoseconds
          : '_nanoseconds' in value && typeof value._nanoseconds === 'number'
            ? value._nanoseconds
            : 0;
      const d = new Date(seconds * 1000 + Math.floor(nanos / 1e6));
      return isValid(d) ? d : null;
    }
  }
  return null;
}

/**
 * Coerce any timestamp-like value into epoch millis, or 0 if it can't be parsed.
 * Safe drop-in for sort comparators that used `x?.toMillis() || 0`.
 */
export function toMillis(value: TimestampLike): number {
  const d = toDate(value);
  return d ? d.getTime() : 0;
}

/**
 * Format date to Hebrew locale. Accepts Firestore Timestamps, plain serialized
 * timestamp objects, ISO strings, epoch millis, or Date — anything non-parseable
 * renders as an empty string rather than throwing.
 */
export function formatDate(date: TimestampLike, formatStr = 'dd/MM/yyyy'): string {
  const dateObj = toDate(date);
  if (!dateObj) return '';
  return format(dateObj, formatStr, { locale: he });
}

// Hebrew weekday letters indexed by Date#getDay() (0 = Sunday .. 6 = Saturday).
const HEBREW_WEEKDAY_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

/**
 * Hebrew weekday label for a date, e.g. "יום א׳" (Sunday) .. "יום ש׳" (Saturday).
 * Accepts the same TimestampLike shapes as formatDate; unparseable input renders
 * as an empty string rather than throwing.
 */
export function formatHebrewWeekday(date: TimestampLike): string {
  const dateObj = toDate(date);
  if (!dateObj) return '';
  return `יום ${HEBREW_WEEKDAY_LETTERS[dateObj.getDay()]}׳`;
}

/**
 * Format date to YYYY-MM-DD (for storage)
 */
export function formatDateISO(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Get today's date in YYYY-MM-DD format
 */
export function getTodayISO(): string {
  return formatDateISO(new Date());
}

/**
 * Format decimal hours as a H:MM duration string.
 * e.g. 8.05 → "8:03", 7.5 → "7:30", 0 → "0:00"
 */
export function formatHoursAsHHMM(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return '0:00';
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/**
 * Parse "H:MM"/"HH:MM" (or a bare integer of hours) into decimal hours.
 * Returns null for invalid input: minutes >= 60, negatives, or more than 24h.
 * e.g. "7:30" → 7.5, "8" → 8
 */
export function parseHHMMToHours(value: string): number | null {
  const match = value.trim().match(/^(\d{1,2})(?::(\d{2}))?$/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const m = match[2] != null ? parseInt(match[2], 10) : 0;
  if (m >= 60) return null;
  const hours = h + m / 60;
  return hours > 24 ? null : hours;
}
